import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import ReactGA4 from 'react-ga4';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type {
  ExtendedReservation,
  JanePrinter,
  MenuProduct,
} from '@jane/shared/models';

import type { AppDispatch, AppState } from '../../customer/redux';
import type { setPreviousPath } from '../../customer/redux/users';
import { SET_PREVIOUS_PATH } from '../../customer/redux/users';
import type { CustomerAction } from '../customerAction';

declare global {
  interface Window {
    ga?: any;
    JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED: boolean;
  }
}

const APPLICATION_OPEN_MODAL = 'application/open-modal';

const APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE =
  'application/add-google-analytics-instance';

type OpenModalParams =
  | { name: 'declineCustomerReservation'; reservation: ExtendedReservation }
  | {
      addRule: (
        type: string,
        options: Array<string>,
        updateField: (value: any) => void
      ) => void;
      mode: string;
      name: 'editSpecialRules';
      nested: boolean;
      options: Array<any>;
      rules: Map<string, string>;
      title: string;
    }
  | {
      addProductsRule: (product: any) => void;
      currentSet: Set<string | number>;
      filterOptions: {
        brands: Array<string>;
        kinds: Array<string>;
        lineages: Array<string>;
      };
      mode: string;
      name: 'addIndividualProducts';
      title: string;
    }
  | { name: 'acceptReservation'; reservation: ExtendedReservation }
  | { name: 'declineReservationAssignment'; reservation: ExtendedReservation }
  | { goBack?: () => void; isCheckoutAlert?: boolean; name: 'cartAlerts' }
  | {
      canSetEquivalency: unknown;
      name: 'editCartLimitRule';
      onSubmit: unknown;
      productTypes: unknown;
      rule: unknown;
      selectedProductTypes: unknown;
      title: unknown;
    }
  | {
      context: 'staff';
      manager: object;
      name: 'editMember';
    }
  | {
      context: 'store';
      manager: object;
      name: 'editMember';
      storeId: number;
    }
  | {
      context: 'store' | 'staff' | string;
      managerId: number;
      name: 'deleteMember';
      storeId?: number;
    }
  | { context: 'staff'; name: 'inviteMember' }
  | { name: 'login' }
  | {
      action: 'create' | 'update';
      dispatch: AppDispatch;
      itemIndex?: number;
      name: 'menuRow';
      productTypes: any;
      ranking?: number;
      row?: any;
      storeId: number | string;
    }
  | { name: 'logout' }
  | { name: 'photoGallery'; photoMetadata: unknown; product: MenuProduct }
  | { name: 'replaceCart' }
  | { name: 'reservationReview' }
  | { name: 'termsAndPrivacy' }
  | { name: 'updateReservation'; newStatus: string; reservation: unknown }
  | { name: 'editPrinter'; onSubmit: unknown; printer: JanePrinter }
  | {
      id: number | string;
      isMember: boolean;
      name: 'toggleCustomersMembership';
    };

export const openModal = createAction<
  {
    disallowClose?: boolean;
  } & OpenModalParams,
  typeof APPLICATION_OPEN_MODAL
>(APPLICATION_OPEN_MODAL);

export const addGoogleAnalyticsInstance = createAction<
  {
    trackingId: string;
  },
  typeof APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE
>(APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE);

const APPLICATION_SET_JANE_DEVICE_ID = 'application/set-jane-device-id';

export const setJaneDeviceId = createAction<
  { janeDeviceId: string },
  typeof APPLICATION_SET_JANE_DEVICE_ID
>(APPLICATION_SET_JANE_DEVICE_ID);

export const APPLICATION_CLOSE_MODAL = 'application/close-modal';
export const closeModal = createAction<void, typeof APPLICATION_CLOSE_MODAL>(
  APPLICATION_CLOSE_MODAL
);

export type ApplicationActions =
  | ReturnType<typeof openModal>
  | ReturnType<typeof closeModal>
  | ReturnType<typeof setPreviousPath>
  | ReturnType<typeof setJaneDeviceId>
  | ReturnType<typeof addGoogleAnalyticsInstance>;

type ApplicationState = DeepReadonly<{
  googleAnalyticsInstance: string | null;
  isModalOpen: boolean;
  janeDeviceId: string | undefined;
  modalName: string;
  modalOptions: any;
  notificationShown: boolean;
}>;

const getInitialState = (): ApplicationState => ({
  isModalOpen: false,
  modalName: '',
  modalOptions: {},
  notificationShown: false,
  janeDeviceId: undefined,
  googleAnalyticsInstance: null,
});

const initializeEcommerceGa = function (
  trackingId: string,
  clientId?: string,
  referrer?: string
) {
  if (trackingId && trackingId.startsWith('UA')) return;

  ReactGA4.initialize(trackingId, {
    testMode: process.env['NODE_ENV'] === 'test',
    gaOptions: {
      cookieFlags: 'SameSite=None; Secure',
      cookieDomain: 'auto',
    },
  });

  try {
    const trackers = window?.ga?.getAll() || [];
    trackers.forEach((tracker: any) => tracker.set('referrer', referrer));
    // eslint-disable-next-line no-empty
  } catch {
  } finally {
    if (referrer) {
      Object.defineProperty(document, 'referrer', {
        get: () => referrer,
      });
    }
  }
};

const removeGa = function () {
  if (window.ga) {
    window.ga('remove');
  }
};

export const applicationReducer: Reducer<ApplicationState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case APPLICATION_OPEN_MODAL: {
      const { name, ...rest } = action.payload;
      return {
        ...state,
        isModalOpen: true,
        modalName: name,
        modalOptions: rest,
      };
    }
    case APPLICATION_CLOSE_MODAL:
      return { ...state, isModalOpen: false, modalName: '', modalOptions: {} };
    case SET_PREVIOUS_PATH: {
      const { janeDeviceId } = state;
      return {
        ...getInitialState(),
        janeDeviceId,
      };
    }
    case APPLICATION_SET_JANE_DEVICE_ID: {
      const { janeDeviceId } = action.payload;
      return {
        ...state,
        janeDeviceId,
      };
    }
    case APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE: {
      const { trackingId } = action.payload;
      if (
        !state.googleAnalyticsInstance ||
        state.googleAnalyticsInstance !== trackingId
      ) {
        Object.defineProperty(
          window,
          'JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED',
          { value: true, writable: false }
        );

        initializeEcommerceGa(trackingId);
        window.addEventListener('message', function (event) {
          /// expects message as follows
          /// frameWindow.postMessage({ messageType: 'initializeGa', payload: { clientId: '123' }}, "*")
          if (
            event.data &&
            event.data.messageType === 'initializeGa' &&
            event.data.payload?.clientId
          ) {
            removeGa();
            initializeEcommerceGa(
              trackingId,
              event.data.payload?.clientId,
              event.data.payload?.referrer
            );
          }
        });

        return {
          ...state,
          googleAnalyticsInstance: trackingId,
        };
      }
    }
  }

  return state;
};

export const selectApplication = (state: AppState) => state.application;
